import { useCallback, useEffect, useState } from "react";
import { HandCoins, Phone } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  Page,
  PageHeader,
  StatCard,
  StatGrid,
  TD,
  TH,
  THead,
  RowActions,
  TR,
  Table,
  TableSkeleton,
  TextInput,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { useCan } from "@/auth/access";

/**
 * What the brand owes its counters — WIREFRAMES.md "Counter settlements".
 *
 * A counter pays a karigar out of its own balance and carries that money until
 * this screen repays it. So the column that matters is not how much a counter
 * has handed out in total, it is how much of it they are still carrying: that
 * is a shopkeeper's own balance sitting in someone else's scheme, and it is the
 * number that decides whether they keep taking part.
 *
 * Counters are ordered by what they are owed, largest first, because the list
 * exists to answer "who do I need to pay" rather than "who is busiest".
 */
interface Row {
  counterId: string;
  counterName: string;
  counterPhone: string | null;
  paidOut: number;
  reimbursed: number;
  outstanding: number;
  outstandingCount: number;
  lastPaidAt: string | null;
}

function qcoin(n: number): string {
  return `${n.toLocaleString("en-IN")} QCoin`;
}

export function SettlementsPage() {
  const toast = useToast();
  const canPay = Boolean(useCan()("settlements", "create"));
  const [rows, setRows] = useState<Row[] | null>(null);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [loading, setLoading] = useState(true);

  const [target, setTarget] = useState<Row | null>(null);
  const [note, setNote] = useState("");
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const res = await api.get<{ items: Row[]; totalOutstanding: number }>("/org/settlements");
      setRows(res.items);
      setTotalOutstanding(res.totalOutstanding);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load settlements.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function settle(): Promise<void> {
    if (!target) return;
    setError(null);
    setPaying(true);
    try {
      const res = await api.post<{ points: number; count: number; displayId: string }>(
        `/org/settlements/${target.counterId}`,
        { note: note.trim() || undefined },
      );
      toast.success(`${qcoin(res.points)} marked as paid to ${target.counterName} (${res.displayId}).`);
      setTarget(null);
      setNote("");
      await load();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not record that payment.");
    } finally {
      setPaying(false);
    }
  }

  const owing = (rows ?? []).filter((r) => r.outstanding > 0).length;

  return (
    <Page bleed>
      <PageHeader title="Counter settlements" />

      {/* The totals keep their breathing room; the table below runs edge to edge. */}
      <div className="shrink-0 px-5 py-4 lg:px-8">
        <StatGrid>
          <StatCard label="Owed to counters" value={qcoin(totalOutstanding)} />
          <StatCard label="Counters waiting" value={rows ? String(owing) : "—"} />
        </StatGrid>
      </div>

      <Card fill>
        {loading && !rows ? (
          <TableSkeleton cols={5} />
        ) : rows && rows.length > 0 ? (
          <Table>
            <THead>
              <TR>
                <TH>Counter</TH>
                <TH align="right">Paid to karigars</TH>
                <TH align="right">Already repaid</TH>
                <TH align="right">You owe</TH>
                <TH align="right" width="4.5rem">
                  <span className="sr-only">Actions</span>
                </TH>
              </TR>
            </THead>
            <tbody>
              {rows.map((r) => (
                <TR key={r.counterId}>
                  <TD>
                    <span className="font-medium text-ink">{r.counterName}</span>
                    {r.counterPhone ? (
                      <span className="flex items-center gap-1.5 text-xs text-muted">
                        <Phone aria-hidden="true" className="size-3" />
                        {r.counterPhone}
                      </span>
                    ) : null}
                  </TD>
                  <TD align="right" className="tabular-nums text-muted">
                    {qcoin(r.paidOut)}
                  </TD>
                  <TD align="right" className="tabular-nums text-muted">
                    {qcoin(r.reimbursed)}
                  </TD>
                  <TD align="right">
                    {r.outstanding > 0 ? (
                      <span className="font-title font-semibold tabular-nums text-ink">
                        {qcoin(r.outstanding)}
                      </span>
                    ) : (
                      <Badge tone="success">Settled up</Badge>
                    )}
                  </TD>
                  <TD align="right">
                    <RowActions
                      items={[
                        {
                          label: "Record payment",
                          icon: HandCoins,
                          hidden: !canPay,
                          disabled: r.outstanding <= 0,
                          onSelect: () => {
                            setTarget(r);
                            setError(null);
                          },
                        },
                      ]}
                      label={r.counterName}
                    />
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState
            message="Once a counter pays a karigar against their code, what you owe them will appear here."
            title="Nothing owed yet"
          />
        )}
      </Card>

      <Modal
        onClose={() => setTarget(null)}
        open={target !== null}
        title={target ? `Pay ${target.counterName}` : ""}
      >
        {target ? (
          <div className="flex flex-col gap-4">
            {/* This screen records a payment, it does not make one. Saying so
                plainly is what stops it being mistaken for a transfer. */}
            <Alert kind="warning" title={`Recording ${qcoin(target.outstanding)} as paid`}>
              This covers {target.outstandingCount} payout
              {target.outstandingCount === 1 ? "" : "s"}. It does not move any money — send the
              transfer yourself, then record it here so the counter can see it.
            </Alert>

            <TextInput
              label="How you paid (optional)"
              onChange={(e) => setNote(e.target.value)}
              placeholder="UPI 4471 8820 3344"
              value={note}
            />

            {error ? <Alert kind="danger">{error}</Alert> : null}

            <div className="flex justify-end gap-2">
              <Button onClick={() => setTarget(null)} type="button" variant="secondary">
                Cancel
              </Button>
              <Button loading={paying} onClick={() => void settle()}>
                Record {qcoin(target.outstanding)} as paid
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </Page>
  );
}
